// src/pages/AdsWall.js
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { db } from "../firebaseConfig";
import { getAuth } from "firebase/auth";
import {
  collection,
  query,
  doc,
  deleteDoc,
  addDoc,
  getDoc,
  getDocs,
  limit,
  startAfter,
  where,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import { useParams, useNavigate } from "react-router-dom";
import useProtectedRoute from "../hooks/useProtectedRoute";
import Button from "../components/Button/Button";
import H1 from "../components/H1/H1";
import Menu from "../components/Menu";
import Footer from "../components/Footer";
import MuralItemSkeleton from "../components/MuralItemSkeleton";
import MuralItem from "../components/MuralItem/MuralItem";
import MuralItemDetailsPopup from "../components/MuralItemDetailsPopup";
import PublishItemPopup from "../components/PublishItemPopup";
import CategoryFilter from "../components/CategoryFilter";
import SelectedFilters from "../components/SelectedFilters";
import FilterBar from "../components/FilterBar/FilterBar";
import ResultsCount from "../components/ResultsCount/ResultsCount";
import infoIcon from "../assets/ico-info.svg";
import "./AdsWall.css";

const ITEMS_PER_PAGE = 16;

const isItemActive = (itemData) => {
  if (itemData.archivedAt) return false;
  if (itemData.isVisible === false) return false;
  if (itemData.expiresAt) {
    const expiresMs =
      itemData.expiresAt instanceof Timestamp
        ? itemData.expiresAt.toMillis()
        : itemData.expiresAt.seconds * 1000;
    if (expiresMs < Date.now()) return false;
  }
  return true;
};

const AdsWall = () => {
  const [hasMoreItems, setHasMoreItems] = useState(true);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedType, setSelectedType] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("BR");
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  // Estado de paginação em refs: muda sem re-render e sem invalidar callbacks.
  const lastVisibleRef = useRef(null);
  const isFetchingRef = useRef(false);
  const hasMoreRef = useRef(true);

  const isLoggedIn = useProtectedRoute();

  const cityOptions = useMemo(() => {
    if (!selectedState) return [];
    const cities = new Set();
    items.forEach((item) => {
      if (item.estado === selectedState && item.cidade) cities.add(item.cidade);
    });
    return Array.from(cities).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [items, selectedState]);

  const auth = getAuth();
  const currentUser = auth.currentUser;
  const userId = currentUser ? currentUser.uid : null;

  const [showPublishPopup, setShowPublishPopup] = useState(false);
  const openPublishPopup = () => setShowPublishPopup(true);

  const { itemId: deepLinkItemId } = useParams();
  const navigate = useNavigate();
  const [deepLinkItem, setDeepLinkItem] = useState(null);
  const [deepLinkAuthor, setDeepLinkAuthor] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!deepLinkItemId || !isLoggedIn) {
      setDeepLinkItem(null);
      setDeepLinkAuthor(null);
      return undefined;
    }
    (async () => {
      try {
        const itemSnap = await getDoc(doc(db, "mural", deepLinkItemId));
        if (!itemSnap.exists()) {
          if (!cancelled) setDeepLinkItem(null);
          return;
        }
        const itemData = { id: itemSnap.id, ...itemSnap.data() };
        if (cancelled) return;
        setDeepLinkItem(itemData);
        if (itemData.userId) {
          const userSnap = await getDoc(doc(db, "users", itemData.userId));
          if (!cancelled && userSnap.exists()) {
            setDeepLinkAuthor({ id: userSnap.id, ...userSnap.data() });
          }
        }
      } catch (err) {
        console.error("Erro ao carregar item compartilhado:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deepLinkItemId, isLoggedIn]);

  const closeDeepLink = () => {
    setDeepLinkItem(null);
    setDeepLinkAuthor(null);
    navigate("/mural-da-rede", { replace: true });
  };

  const matchesLocationFilters = useCallback(
    (data) => {
      if (selectedCountry && data.country && data.country !== selectedCountry)
        return false;
      if (selectedState && data.estado !== selectedState) return false;
      if (selectedCity && data.cidade !== selectedCity) return false;
      return true;
    },
    [selectedCountry, selectedState, selectedCity],
  );

  const fetchPage = useCallback(
    async ({ reset = false } = {}) => {
      if (!isLoggedIn) return;
      if (isFetchingRef.current) return;
      if (!reset && !hasMoreRef.current) return;

      isFetchingRef.current = true;
      setLoading(true);

      try {
        // Apenas category/type vão para o servidor (índices simples + 1 composto).
        // País/Estado/Cidade são filtrados no cliente para evitar a explosão
        // combinatória de índices e suportar itens legados sem `country`.
        let q = query(collection(db, "mural"), orderBy("createdAt", "desc"));
        if (selectedCategory)
          q = query(q, where("category", "==", selectedCategory));
        if (selectedType) q = query(q, where("type", "==", selectedType));
        if (!reset && lastVisibleRef.current) {
          q = query(q, startAfter(lastVisibleRef.current));
        }
        q = query(q, limit(ITEMS_PER_PAGE));

        const snapshot = await getDocs(q);

        console.log(
          "[Mural da Rede] Documentos crus do Firestore:",
          snapshot.docs.map((d) => ({ id: d.id, ...d.data() })),
        );

        const user = auth.currentUser;
        const userIdsToFetch = new Set();
        const rawItems = [];

        for (const docSnap of snapshot.docs) {
          const itemData = docSnap.data();
          if (!isItemActive(itemData)) continue;
          if (!matchesLocationFilters(itemData)) continue;
          rawItems.push({ id: docSnap.id, data: itemData });
          if (itemData.userId) userIdsToFetch.add(itemData.userId);
        }

        const userDataById = {};
        await Promise.all(
          Array.from(userIdsToFetch).map(async (uid) => {
            const userSnap = await getDoc(doc(db, "users", uid));
            userDataById[uid] = userSnap.exists() ? userSnap.data() : {};
          }),
        );

        const favoriteIds = new Set();
        if (user && rawItems.length > 0) {
          const favSnap = await getDocs(
            query(collection(db, "favorites"), where("userId", "==", user.uid)),
          );
          favSnap.forEach((favDoc) => {
            const data = favDoc.data();
            if (data.muralItemId) favoriteIds.add(data.muralItemId);
          });
        }

        const newItems = rawItems.map(({ id, data }) => {
          const userData = userDataById[data.userId] || {};
          return {
            id,
            ...data,
            userName: userData.institutionName || userData.firstName,
            userAvatar: userData.avatar,
            userCity: userData.address?.city,
            userState: userData.address?.state,
            isFavorite: favoriteIds.has(id),
          };
        });

        console.log("[Mural da Rede] Dados dos autores (users):", userDataById);
        console.log("[Mural da Rede] Itens montados para o Mural:", newItems);

        lastVisibleRef.current =
          snapshot.docs[snapshot.docs.length - 1] || lastVisibleRef.current;
        const hasMore = snapshot.docs.length >= ITEMS_PER_PAGE;
        hasMoreRef.current = hasMore;
        setHasMoreItems(hasMore);

        setItems((prev) => (reset ? newItems : [...prev, ...newItems]));
      } catch (error) {
        console.error("Error fetching items:", error);
      } finally {
        isFetchingRef.current = false;
        setLoading(false);
      }
    },
    [isLoggedIn, selectedCategory, selectedType, matchesLocationFilters, auth],
  );

  // Carrega somente os itens favoritados do usuário. Como os favoritos podem
  // estar além das páginas já carregadas, buscamos direto a coleção `favorites`
  // (conjunto limitado) e aplicamos os demais filtros no cliente. Sem paginação.
  const fetchFavorites = useCallback(async () => {
    if (!isLoggedIn) return;
    const user = auth.currentUser;
    if (!user) return;
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    setLoading(true);

    try {
      const favSnap = await getDocs(
        query(collection(db, "favorites"), where("userId", "==", user.uid)),
      );
      const favoriteIds = [];
      favSnap.forEach((favDoc) => {
        const data = favDoc.data();
        if (data.muralItemId) favoriteIds.push(data.muralItemId);
      });

      if (favoriteIds.length === 0) {
        setItems([]);
        return;
      }

      const docs = await Promise.all(
        favoriteIds.map((id) => getDoc(doc(db, "mural", id))),
      );

      const userIdsToFetch = new Set();
      const rawItems = [];
      docs.forEach((snap) => {
        if (!snap.exists()) return;
        const itemData = snap.data();
        if (!isItemActive(itemData)) return;
        if (selectedCategory && itemData.category !== selectedCategory) return;
        if (selectedType && itemData.type !== selectedType) return;
        if (!matchesLocationFilters(itemData)) return;
        rawItems.push({ id: snap.id, data: itemData });
        if (itemData.userId) userIdsToFetch.add(itemData.userId);
      });

      const userDataById = {};
      await Promise.all(
        Array.from(userIdsToFetch).map(async (uid) => {
          const userSnap = await getDoc(doc(db, "users", uid));
          userDataById[uid] = userSnap.exists() ? userSnap.data() : {};
        }),
      );

      const newItems = rawItems.map(({ id, data }) => {
        const userData = userDataById[data.userId] || {};
        return {
          id,
          ...data,
          userName: userData.institutionName || userData.firstName,
          userAvatar: userData.avatar,
          userCity: userData.address?.city,
          userState: userData.address?.state,
          isFavorite: true,
        };
      });

      // Mesma ordenação do mural (mais recentes primeiro).
      newItems.sort(
        (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0),
      );

      setItems(newItems);
    } catch (error) {
      console.error("Error fetching favorites:", error);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [isLoggedIn, selectedCategory, selectedType, matchesLocationFilters, auth]);

  // Quando os filtros mudam (ou o login é resolvido), zera paginação e busca a primeira página.
  useEffect(() => {
    if (!isLoggedIn) return;
    lastVisibleRef.current = null;
    hasMoreRef.current = true;
    setItems([]);
    setHasMoreItems(!showFavoritesOnly);
    if (showFavoritesOnly) {
      fetchFavorites();
    } else {
      fetchPage({ reset: true });
    }
  }, [fetchPage, fetchFavorites, showFavoritesOnly, isLoggedIn]);

  const loadMoreItems = () => fetchPage({ reset: false });

  const removeItemFromState = (itemId) => {
    setItems((prevItems) => prevItems.filter((item) => item.id !== itemId));
  };

  const handleCategorySelect = useCallback((category) => {
    setSelectedCategory(category);
  }, []);

  const toggleFavorite = async (itemId) => {
    const user = auth.currentUser;
    if (!user) return;

    const favoritesRef = collection(db, "favorites");
    const q = query(
      favoritesRef,
      where("userId", "==", user.uid),
      where("muralItemId", "==", itemId),
    );
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      await deleteDoc(snapshot.docs[0].ref);
    } else {
      await addDoc(favoritesRef, { userId: user.uid, muralItemId: itemId });
    }

    setItems((prev) => {
      // No modo "somente favoritos", desfavoritar (snapshot não-vazio) remove o item da lista.
      if (showFavoritesOnly && !snapshot.empty) {
        return prev.filter((item) => item.id !== itemId);
      }
      return prev.map((item) =>
        item.id === itemId ? { ...item, isFavorite: snapshot.empty } : item,
      );
    });
  };

  if (!isLoggedIn) {
    return null;
  }

  const clearFilters = () => {
    setSelectedCategory(null);
    setSelectedType("");
    setSelectedCountry("BR");
    setSelectedState("");
    setSelectedCity("");
    setShowFavoritesOnly(false);
  };

  const handleTypeChange = (e) => setSelectedType(e.target.value);

  const handleCountryChange = (e) => {
    setSelectedCountry(e.target.value);
    setSelectedState("");
    setSelectedCity("");
  };

  const handleStateChange = (e) => {
    setSelectedState(e.target.value);
    setSelectedCity("");
  };

  const handleCityChange = (e) => setSelectedCity(e.target.value);

  const handleFavoritesToggle = (checked) => setShowFavoritesOnly(checked);

  //console.log("Items carregados:", items);

  return (
    <>
      <Menu isLoggedIn={isLoggedIn} />
      <div className="ads-wall-container">
        <div className="header-container">
          <H1 text="Mural da Rede" bgColor="purple" fontColor="white" />
          <div className="header-message-container">
            <img src={infoIcon} alt="Info" className="info-icon" />
            <p className="header-message">
              A Rede Refugia só é possível com a sua publicação. Não deixe de{" "}
              <span className="post-link" onClick={openPublishPopup}>
                postar a sua oferta ou demanda.
              </span>
            </p>
          </div>
        </div>
        <CategoryFilter onCategorySelect={handleCategorySelect} />

        <div className="flex items-center justify-between py-4 px-0">
          <div className="flex items-center">
            <FilterBar
              selectedType={selectedType}
              handleTypeChange={handleTypeChange}
              selectedCountry={selectedCountry}
              handleCountryChange={handleCountryChange}
              selectedState={selectedState}
              handleStateChange={handleStateChange}
              selectedCity={selectedCity}
              handleCityChange={handleCityChange}
              cityOptions={cityOptions}
              showFavoritesOnly={showFavoritesOnly}
              handleFavoritesToggle={handleFavoritesToggle}
            />
          </div>
          <div className="flex items-center">
            <ResultsCount count={items.length} />
          </div>
        </div>

        <SelectedFilters
          selectedCategory={selectedCategory}
          onClearFilters={clearFilters}
        />
        <div className="ads-wall-grid">
          {loading
            ? Array.from({ length: ITEMS_PER_PAGE }, (_, index) => (
                <MuralItemSkeleton key={index} />
              ))
            : items.map((item) => (
                <MuralItem
                  key={item.id}
                  item={item}
                  onToggleFavorite={toggleFavorite}
                  removeItemFromState={removeItemFromState}
                />
              ))}
        </div>
        {!loading && showFavoritesOnly && items.length === 0 && (
          <p className="text-gray-500 mt-4">
            Você ainda não favoritou nenhuma oferta ou demanda.
          </p>
        )}
        {loading && <p>Loading...</p>}
        {!loading && hasMoreItems && (
          <div className="flex justify-center mt-4">
            <Button
              text="Carregar mais"
              variant="solid"
              onClick={loadMoreItems}
            />
          </div>
        )}
      </div>
      {isLoggedIn && showPublishPopup && (
        <PublishItemPopup
          userId={userId}
          onClose={() => setShowPublishPopup(false)}
        />
      )}
      {deepLinkItem && (
        <MuralItemDetailsPopup
          item={deepLinkItem}
          authorInfo={deepLinkAuthor}
          onClose={closeDeepLink}
          onSendMessage={() => {}}
          onToggleFavorite={() => toggleFavorite(deepLinkItem.id)}
        />
      )}
      <Footer />
    </>
  );
};

export default AdsWall;
